import { Component, type ErrorInfo, type ReactNode } from "react";
import { Sentry, trackError } from "@dwelldrop/shared";
import { getSupabase } from "@dwelldrop/supabase";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const isDev = import.meta.env.MODE === "development";

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console first
    console.error("[ErrorBoundary] Uncaught error:", error, errorInfo);

    // Try to send to Sentry, but don't crash if it fails
    try {
      Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    } catch (sentryError) {
      console.warn("[ErrorBoundary] Failed to send error to Sentry:", sentryError);
    }

    // Track in error_events table
    trackError(getSupabase(), {
      source: "venue",
      category: "unhandled_exception",
      severity: "critical",
      message: error.message,
      error,
      metadata: { componentStack: errorInfo.componentStack },
    });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
    // Also reload the page to reset state
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            padding: "2rem",
            textAlign: "center",
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}
        >
          <h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>
            Something went wrong
          </h1>
          <p style={{ color: "#6b7280", marginBottom: "1.5rem" }}>
            An unexpected error occurred. Please try again.
          </p>
          {isDev && this.state.error && (
            <div
              style={{
                textAlign: "left",
                backgroundColor: "#1f2937",
                color: "#f3f4f6",
                padding: "1rem",
                borderRadius: "0.5rem",
                maxWidth: "600px",
                width: "100%",
                marginBottom: "1.5rem",
                fontSize: "0.875rem",
                overflow: "auto",
              }}
            >
              <p style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>Error Details:</p>
              <p style={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
                {this.state.error.toString()}
              </p>
              {this.state.error.stack && (
                <details style={{ marginTop: "0.5rem" }}>
                  <summary style={{ cursor: "pointer" }}>Stack Trace</summary>
                  <pre style={{ marginTop: "0.5rem", whiteSpace: "pre-wrap", fontSize: "0.75rem" }}>
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
            </div>
          )}
          <button
            onClick={this.handleRetry}
            style={{
              padding: "0.5rem 1.5rem",
              backgroundColor: "#2563EB",
              color: "#fff",
              border: "none",
              borderRadius: "0.375rem",
              cursor: "pointer",
              fontSize: "1rem",
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
